// src/components/Usecase.js
import React, { useState, useRef, useEffect } from "react";

import { Link } from "react-router-dom";

// --- Data for Use Cases (Using Imported Videos) ---
const useCasesData = [
  {
    title: "Financial Services",
    description: "Transforming Financial Services by Overcoming Legacy Challenges.",
    videoUrl: "https://zenithblox.blob.core.windows.net/videos/financial_services.mp4?sp=r&st=2025-06-11T15:34:06Z&se=2060-06-11T23:34:06Z&spr=https&sv=2024-11-04&sr=b&sig=BJPm4sl%2FOfm1OTXbkcrl2IVHOzNucWaYEG3f0093C2Y%3D",
    key: "financial",
  },
  {
    title: "Supply Chain",
    description: "Improving Supply Chain Transparency to Fight Fraud and Counterfeits.",
    videoUrl: "https://zenithblox.blob.core.windows.net/videos/supply_chain.mp4?sp=r&st=2025-06-11T15:36:58Z&se=2060-06-11T23:36:58Z&spr=https&sv=2024-11-04&sr=b&sig=gvqHZHR6yY%2FuY0%2FngnhXHI4XqCaCuEQB7%2BDEuWnCias%3D",
    key: "supplychain",
  },
  {
    title: "Healthcare",
    description: "Enhancing Secure Patient Data Management in Healthcare.",
    videoUrl: "https://zenithblox.blob.core.windows.net/videos/healthcare.mp4?sp=r&st=2025-06-11T15:36:28Z&se=2060-06-11T23:36:28Z&spr=https&sv=2024-11-04&sr=b&sig=%2BwwcPhQ3lvhu86qSHQi3W1j160AXl8XZcIbsuTWGuKI%3D",
    key: "healthcare",
  },
  {
    title: "Government",
    description: "Enhancing Public Service Transparency, Security, and Accountability.",
    videoUrl: "https://zenithblox.blob.core.windows.net/videos/government.mp4?sp=r&st=2025-06-11T15:35:50Z&se=2060-06-11T23:35:50Z&spr=https&sv=2024-11-04&sr=b&sig=GCENSYDiKbjq5PZHjK6nreYu1Ff5BDvHWjlpsRuYs1g%3D",
    key: "government",
  },
];

// --- Usecase Component ---
const UseCase = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const currentUseCase = useCasesData[activeIndex];

  const handleSelectUseCase = (index) => {
    if (index !== activeIndex) {
      setActiveIndex(index);
      setIsPlaying(false);
    }
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play().catch((error) => console.error("Play failed:", error));
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      setIsPlaying(false);
    }
  }, [activeIndex]);

  const PlayIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 5V19L19 12L8 5Z" />
    </svg>
  );

  const PauseIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 19H10V5H6V19ZM14 5V19H18V5H14Z" />
    </svg>
  );

  return (
    <div className="bg-white text-black py-16 md:py-24 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-end gap-6 mb-12 md:mb-16">
          {/* Left Side: Title and Pill */}
          <div className="text-left w-full lg:max-w-3xl">
            <span className="w-fit inline-block border border-purple-600 text-purple-600 rounded-full px-4 py-1 text-sm font-medium mb-4">
              Use Cases
            </span>
            {/* Content is kept as requested */}
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight">
              Enterprise-proven
              <br />
              and industry-tested.
            </h1>
          </div>

          {/* Right Side: Button */}
          <div className="w-full lg:w-auto flex-shrink-0">
            <Link
              to="/use-cases"
              className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-800 bg-white hover:bg-gray-50 transition-colors"
            >
              Discover All Use Cases &rarr;
            </Link>
          </div>
        </div>
        {/* --- End of Header --- */}

        {/* --- Main Content --- */}
        <div className="bg-black text-white rounded-2xl p-6 sm:p-8 md:p-12 flex flex-col lg:flex-row justify-between items-center min-h-[500px] gap-10 lg:gap-16">
          {/* Left Side: Content */}
          <div className="flex-1 w-full lg:w-auto lg:max-w-md text-left order-2 lg:order-1">
            <h2 className="text-4xl md:text-5xl font-semibold mb-5 transition-opacity duration-500 ease-in-out">{currentUseCase.title}</h2>
            <p className="text-lg leading-relaxed mb-8 text-gray-400 transition-opacity duration-500 ease-in-out">{currentUseCase.description}</p>
            <Link
              to="/use-cases"
              className="bg-transparent text-white border border-white rounded-md py-3 px-7 text-base cursor-pointer transition duration-200 hover:bg-white hover:text-black"
            >
              Explore &rarr;
            </Link>
          </div>

          {/* Right Side: Player Area */}
          <div className="flex-1 w-full lg:w-auto flex flex-col items-center order-1 lg:order-2">
            {/* Video Player */}
            <div className="w-full aspect-video rounded-lg mb-5 relative group overflow-hidden bg-black">
              <video
                ref={videoRef}
                key={currentUseCase.videoUrl}
                className="w-full h-full object-cover rounded-lg"
                src={currentUseCase.videoUrl}
                loop
                playsInline
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
              >
                Your browser does not support the video tag.
              </video>
              {!isPlaying && (
                <div className="absolute inset-0 flex justify-center items-center bg-black/30 cursor-pointer" onClick={togglePlayPause}>
                  <div className="bg-black/60 text-white w-16 h-16 md:w-20 md:h-20 rounded-full flex justify-center items-center text-3xl md:text-4xl border-2 border-white/30 group-hover:scale-110 transition-transform duration-200">
                    <PlayIcon />
                  </div>
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-center bg-gray-900 py-2.5 px-5 rounded-full w-auto">
              <button
                onClick={togglePlayPause}
                className="bg-none border-none text-white text-2xl cursor-pointer mr-4 p-1 hover:text-gray-300 transition"
              >
                {isPlaying ? <PauseIcon /> : <PlayIcon />}
              </button>
              <div className="flex gap-3">
                {useCasesData.map((_, index) => (
                  <span
                    key={useCasesData[index].key}
                    className={`block w-2.5 h-2.5 rounded-full cursor-pointer transition duration-200 ${
                      index === activeIndex ? "bg-white scale-110" : "bg-gray-600 hover:bg-gray-400"
                    }`}
                    onClick={() => handleSelectUseCase(index)}
                  ></span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UseCase;
